import {ButtonActionProps, Card, DataStatus, FieldValues, FormControl} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {Path} from 'react-hook-form';

import {isNotEmpty} from 'ramda';

import {SimilarVehicleResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {VehicleRecommendationType} from '../../../types/VehicleRecommendationType';
import {useSimilarVehicles} from '../hooks/useSimilarVehicles';
import {VehiclePreview, VehicleSelectionType} from './VehiclePreview';

type SimilarVehiclesProps<TFieldValues extends FieldValues> = RequiredTestIdProps & {
  control: FormControl<TFieldValues>;
  hideChooseButton?: boolean;
  additionalActions?: (vehicle: SimilarVehicleResponseBody) => ButtonActionProps[];
  vinField: {
    name: Path<TFieldValues>;
    label?: string;
  };
  registrationPlateField: {
    name: Path<TFieldValues>;
    label?: string;
  };
  shouldRecommendVehiclesFor?: VehicleRecommendationType;
  onVehicleSelect: (
    vehicle: SimilarVehicleResponseBody,
    selectionType: VehicleSelectionType
  ) => void;
  excludeFromResultVehicleIds?: string[];
};

export function SimilarVehicles<TFieldValues extends FieldValues = FieldValues>(
  props: SimilarVehiclesProps<TFieldValues>
) {
  const {
    data: similarVehicles,
    isError,
    isFetching,
    isLoading,
  } = useSimilarVehicles({
    control: props.control,
    vinField: props.vinField,
    registrationPlateField: props.registrationPlateField,
  });

  const filteredSimilarVehicles = similarVehicles.filter(
    (vehicle) => !props.excludeFromResultVehicleIds?.includes(vehicle.id)
  );

  return (
    <Show when={isNotEmpty(filteredSimilarVehicles) || isFetching || isLoading}>
      <Card
        variant="inlineGrey"
        data-testid={suffixTestId('similarVehicles', props)}
        title={!isFetching ? i18n.t('entity.vehicle.labels.matchingVehicles') : undefined}
      >
        <DataStatus isLoading={isFetching} isError={isError}>
          <VStack spacing={2}>
            {filteredSimilarVehicles.map((vehicle, index) => (
              <VehiclePreview
                onSelect={(selectionType) => props.onVehicleSelect(vehicle, selectionType)}
                vehicle={vehicle}
                key={vehicle.id}
                shouldRecommendVehiclesFor={props.shouldRecommendVehiclesFor}
                hideChooseButton={props.hideChooseButton}
                data-testid={suffixTestId(`[${index}]`, props)}
                additionalActions={props.additionalActions}
              />
            ))}
          </VStack>
        </DataStatus>
      </Card>
    </Show>
  );
}
